import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import { setHours, setMinutes, setSeconds, setMilliseconds } from "date-fns";
import { formatISO } from "date-fns";

import Button from "components/Buttons";
import Icon from "components/Icon/Icon";
import { RosterUpdateModalStyles } from "./styled";
import Input from "components/Input/index";
import Select from "components/Select/index";
import {
  generateTimeOptions,
  getCurrentRoundedTime,
  isSameDay
} from "utils/helper";
import {
  CaretLeftIcon,
  CaretRightIcon,
  CalendarBlankIcon,
  X
} from "@phosphor-icons/react";
import { useCompaniesSlice } from "pages/Companies/slice/index";
import { useDispatch } from "react-redux";

const RosterUpdateModal = ({ show, onHide, onUpdate }) => {
  const { actions } = useCompaniesSlice();
  const dispatch = useDispatch();
  const { agencyId } = useParams();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(getCurrentRoundedTime(15));
  const [isDateOpen, setIsDateOpen] = useState(false);

  const showMonth = 1;
  const [dateRange, setDateRange] = useState([null, null]);
  const [start, end] = dateRange;
  const today = new Date();

  const dateRef = useRef(null);

  // -----------------------------
  // FILTER TIME OPTIONS
  // -----------------------------
  const timeOptions = useMemo(() => {
    const options = generateTimeOptions(15);

    // If NOT today → show all times
    if (!isSameDay(selectedDate, today)) {
      return options;
    }

    // Today → hide future times
    const currentRounded = getCurrentRoundedTime(15);
    return options.filter((opt) => opt.value <= currentRounded);
  }, [selectedDate]);

  useEffect(() => {
    if (show) {
      setSelectedDate(new Date());
      setSelectedTime(getCurrentRoundedTime(15));
    }
  }, [show]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dateRef.current && !dateRef.current.contains(e.target)) {
        setIsDateOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleUpdate = () => {
    if (!selectedDate || !selectedTime) return;

    const [time, meridian] = selectedTime.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (meridian === "PM" && hours !== 12) hours += 12;
    if (meridian === "AM" && hours === 12) hours = 0;

    const finalDate = new Date(selectedDate);
    finalDate.setHours(hours, minutes, 0, 0);

    // Send in UTC ISO format
    const formData = new FormData();
    formData.append("roster_updated_at", finalDate.toISOString());

    dispatch(
      actions.updateCompanyDetails({
        companyId: agencyId,
        formData
      })
    );

    onHide();
  };

  return (
    <RosterUpdateModalStyles
      show={show}
      onHide={onHide}
      backdrop={false}
      keyboard={true}
      centered
      className="centered-modal"
    >
      {/* HEADER */}
      <Modal.Header>
        <Modal.Title>Roster Updated at</Modal.Title>
        <div onClick={onHide} className="close-icon-wrapper">
          <Icon component={X} size={24} />
        </div>
      </Modal.Header>

      {/* BODY */}
      <Modal.Body>
        <p className="sub-text">
          Select the date and time the roster was last updated.
        </p>

        <div className="fields-row">
          {/* DATE FIELD */}
          <div className="field date-field" ref={dateRef}>
            <Input
              id="select_date"
              label="Select Date"
              name="select_date"
              type="text"
              size="M"
              required
              value={selectedDate ? selectedDate.toLocaleDateString() : ""}
              rightIcon={
                <Icon
                  component={CalendarBlankIcon}
                  size={16}
                  weight="regular"
                  color="#A5A5A5"
                />
              }
              placeholder="MM/DD/YYYY"
              readOnly
              onClick={() => setIsDateOpen(true)}
            />

            {isDateOpen && (
              <div className="date-dropdown">
                <DatePicker
                  className="single-month-picker"
                  calendarClassName="calendar-single-month"
                  selected={selectedDate}
                  maxDate={today}
                  inline
                  onChange={(date) => {
                    setSelectedDate(date); // update input
                    setIsDateOpen(false); // close picker
                  }}
                  renderCustomHeader={({
                    monthDate,
                    decreaseMonth,
                    increaseMonth
                  }) => {
                    const isFutureMonth =
                      monthDate.getFullYear() > today.getFullYear() ||
                      (monthDate.getFullYear() === today.getFullYear() &&
                        monthDate.getMonth() > today.getMonth());

                    const isNextMonthDisabled =
                      monthDate.getMonth() === today.getMonth() &&
                      monthDate.getFullYear() === today.getFullYear();

                    return (
                      <div className="custom-datepicker-header">
                        <div onClick={decreaseMonth} className="nav-arrow prev">
                          <Icon component={CaretLeftIcon} size={16} />
                        </div>

                        <span className="month-label">
                          {monthDate.toLocaleString("default", {
                            month: "long",
                            year: "numeric"
                          })}
                        </span>

                        {!isFutureMonth && !isNextMonthDisabled && (
                          <div
                            onClick={increaseMonth}
                            className="nav-arrow next"
                          >
                            <Icon component={CaretRightIcon} size={16} />
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </div>

          {/* TIME FIELD */}
          <div className="field">
            <Select
              id="choose_time"
              label="Choose Time"
              placeholder="Select Time"
              options={timeOptions}
              value={selectedTime}
              onChange={(val) => setSelectedTime(val)}
            />
          </div>
        </div>
      </Modal.Body>

      {/* FOOTER */}
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          disabled={!selectedDate || !selectedTime}
          onClick={handleUpdate}
        >
          Update
        </Button>
      </Modal.Footer>
    </RosterUpdateModalStyles>
  );
};

export default RosterUpdateModal;
